import { FC } from "react";
import { BLANK_GIF, useCompanyLogo, useLogosConfig } from "@/lib/logos";
import { IrForecastRow } from "@/models/index-rebalance-v2";

type LogoRow = Pick<IrForecastRow, "isin" | "ticker" | "exchange" | "locked">;

/**
 * A company logo for an Index Rebalance row, from the platform's logo service (`src/lib/logos.ts`,
 * the same hook the Pulse rows and data grids use). Lazy-loaded; nothing at all when the company
 * has no logo, never a broken image. While the logo service is not resolved yet (always the case in
 * the server's HTML) the box is drawn empty, so the logo arriving does not grow every row. A locked
 * row shows no logo: it would give away the name the blur is hiding.
 */
const IrLogo: FC<{ row: LogoRow; size?: number }> = ({ row, size = 24 }) => {
    useLogosConfig(); // re-render when the logo source flips or the token rotates
    const logo = useCompanyLogo(row.locked ? null : { isin: row.isin, ticker: row.ticker, exchange: row.exchange }, "rect");
    if (row.locked || logo.failed) return null;
    const pending = logo.src === BLANK_GIF;
    // The wide ("rect") artwork in a box twice as wide as tall, left-aligned: the "square" file pads a
    // wordmark into a sliver, this one reads at list size, and a square mark still lines tickers up.
    const width = Math.round(size * 2);
    return (
        <span
            aria-hidden="true"
            style={{
                display: "inline-flex",
                alignItems: "center",
                width,
                height: size,
                flex: `0 0 ${width}px`,
                verticalAlign: "middle",
                marginRight: Math.round(size / 3),
            }}
        >
            {/* A plain lazy <img>, as PulseRow does: next/image adds nothing for a small icon from the logo service. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            {!pending && (
                <img
                    src={logo.src}
                    alt=""
                    height={size}
                    loading="lazy"
                    decoding="async"
                    onError={logo.onError}
                    onLoad={logo.onLoad}
                    style={{
                        maxWidth: "100%",
                        height: "100%",
                        objectFit: "contain",
                        objectPosition: "left center",
                        borderRadius: Math.round(size / 5),
                        background: logo.plate ?? "transparent",
                    }}
                />
            )}
        </span>
    );
};

export default IrLogo;
